import { baseUrl, httpClient } from 'src/pages/admin/providers/AppDataProvider';
import { getAuth } from 'src/pages/admin/providers/AppAuthProvider';

import type {
  BatchCreateManualIssueGrantPayload,
  CreateManualIssueGrantPayload,
  ManualIssueGrant,
  ManualIssueGrantListQuery,
  ManualIssueGrantListResponse,
  ManualIssueStats,
} from './types';
import { normalizeGrantList, normalizeStats } from './utils';

const resourceBase = 'world-cup/voucher-manual-issue';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { json } = await httpClient(`${baseUrl}/${resourceBase}${path}`, options as any);
  return json as T;
}

function buildQueryString(query: ManualIssueGrantListQuery = {}) {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.append(key, String(value));
  });

  const search = params.toString();
  return search ? `?${search}` : '';
}

export const manualIssueApi = {
  async getStats(signal?: AbortSignal): Promise<ManualIssueStats> {
    const data = await request<unknown>('/stats', {
      method: 'GET',
      signal,
    } as any);

    return normalizeStats(data);
  },

  async listGrants(
    query: ManualIssueGrantListQuery = {},
    signal?: AbortSignal
  ): Promise<ManualIssueGrantListResponse> {
    const data = await request<unknown>(`/grants${buildQueryString(query)}`, {
      method: 'GET',
      signal,
    } as any);

    return normalizeGrantList(data, query.limit ?? 10);
  },

  async createGrant(payload: CreateManualIssueGrantPayload): Promise<ManualIssueGrant | null> {
    const data = await request<unknown>('/grants', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!data || typeof data !== 'object') return null;
    return data as ManualIssueGrant;
  },

  async batchCreateGrant(payload: BatchCreateManualIssueGrantPayload): Promise<unknown> {
    return request<unknown>('/batch-issue', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async exportGrants(): Promise<void> {
    const { accessToken } = (await getAuth()) ?? {};
    const url = `${baseUrl}/${resourceBase}/export`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

    if (!res.ok) {
      throw new Error(`Export failed: ${res.status} ${res.statusText}`);
    }

    const blob = await res.blob();

    const cd = res.headers.get('Content-Disposition') ?? '';
    const filename = /filename="(.+?)"/.exec(cd)?.[1] ?? 'manual-voucher-grants.xlsx';
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
  },
};
